/* Copyright (c) 2020 IceRock MAG Inc. Use of this source code is governed by the Apache 2.0 license. */

import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { Container, CssBaseline, ThemeProvider, WithStyles, withStyles } from '@material-ui/core';
import DateFnsAdapter from '@material-ui/pickers/adapter/date-fns'; // choose your lib
import { LocalizationProvider } from '@material-ui/pickers';
import ruLocale from 'date-fns/locale/ru';
import { Navigation } from 'icerockdev-admin-toolkit/dist/containers/layout/Navigation';
import { Config, PageRenderer } from 'icerockdev-admin-toolkit';
import styles from '~/application/styles';
import { BASE_URL } from '~/config';
import { OAuthLogin } from '~/config/auth/OAuthLogin';
import { useJiraOAuth } from '~/config/auth/OAuthHook';
import OAuth2AuthProvider from '~/config/auth/OAuth2AuthProvider';
import { OAuthInProgress } from '~/config/auth/OAuthInProgress';

type IProps = WithStyles<typeof styles> & {
  config: Config;
};

const Application = withStyles(styles)(
  observer(({ classes, config }: IProps) => {
    const links = useMemo(
      () =>
        config.pages
          .filter((page) => page?.menu?.url && page.canList)
          .map((page) => ({
            name: page.menu.label,
            url: page.menu.url,
          })),
      [config.pages, config.auth?.user?.role]
    );

    const role = useMemo(
      () =>
        (config.auth?.roleTitles &&
          config.auth?.user?.role &&
          config.auth?.roleTitles[config.auth?.user?.role]) ||
        config.auth?.user?.role,
      [config.auth?.roleTitles, config.auth?.user?.role]
    );

    const oauthProvider: OAuth2AuthProvider = config.auth as OAuth2AuthProvider;

    useJiraOAuth(oauthProvider);

    if (oauthProvider.isLoading) {
      return (
        <ThemeProvider theme={config.theme}>
          <CssBaseline />

          <OAuthInProgress />

          <config.notifications.Output />
        </ThemeProvider>
      );
    } else if (!oauthProvider.isLogged) {
      return (
        <ThemeProvider theme={config.theme}>
          <CssBaseline />

          <OAuthLogin
            onSubmit={() => {
              window.location.href = BASE_URL + '/oauth/login';
            }}
          />

          <config.notifications.Output />
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider theme={config.theme}>
        <LocalizationProvider dateAdapter={DateFnsAdapter} locale={ruLocale}>
          <CssBaseline />

          <Router history={config.history}>
            <Navigation
              links={links}
              logo={{ url: config.logo, title: config.title }}
              account={{
                email: config.auth?.user?.email || '',
                username: config.auth?.user?.username || '',
                role,
              }}
              onLogout={config.auth?.logout}
            />

            <Container maxWidth="xl" className={classes.wrapper}>
              <Switch>
                {config.pages
                  .filter((page) => page?.menu?.url)
                  .map((page) => (
                    <Route
                      path={page.menu.url}
                      render={() => <PageRenderer page={page} />}
                      key={page.menu.url}
                    />
                  ))}
                {links.length > 0 && <Redirect to={links[0].url} />}
              </Switch>
            </Container>
          </Router>

          <config.notifications.Output />
        </LocalizationProvider>
      </ThemeProvider>
    );
  })
);

export { Application };
